import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { useParams, useNavigate } from 'react-router';
import { ViewOrganizationMemberDocument, ViewOrganizationMemberQuery, GetQuestionsDocument, GetQuestionsQuery } from 'graphql/queries.generated';
import { SetOrganizationRoleDocument } from 'graphql/mutations.generated';
import Image from 'components/Image';
import { ImageOwnerType, ImageSize, Permission } from 'app-constants';
import { HasPermission } from 'components/HasPermission';
import formStyles from 'pages/Form.module.css';
import globalStyles from 'Global.module.css';
import styles from 'pages/ViewMemberProfile.module.css';

const ViewMemberProfile: React.FC = () => {
    const { organizationId, organizationMemberId } = useParams<{ organizationId: string; organizationMemberId: string }>();
    const navigate = useNavigate();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [selectedRole, setSelectedRole] = useState<'organizer' | 'eventHost' | null>(null);

    const { data: memberData, loading: memberLoading, error: memberError } = useQuery<ViewOrganizationMemberQuery>(ViewOrganizationMemberDocument, {
        variables: {
            organizationMemberId: organizationMemberId || ''
        },
        skip: !organizationMemberId
    });

    const { data: questionsData, loading: questionsLoading } = useQuery<GetQuestionsQuery>(GetQuestionsDocument, {
        variables: {
            organizationId: memberData?.organizationMember?.organizationId || ''
        },
        skip: !memberData?.organizationMember?.organizationId
    });

    const [setOrganizationRole] = useMutation(SetOrganizationRoleDocument, {
        refetchQueries: [ViewOrganizationMemberDocument]
    });

    const handleOpenDialog = () => {
        if (member) {
            if (member.isOrganizer) {
                setSelectedRole('organizer');
            } else if (member.isEventHost) {
                setSelectedRole('eventHost');
            } else {
                setSelectedRole(null);
            }
        }
        setIsDialogOpen(true);
    };

    const handleSaveRole = async () => {
        if (!organizationMemberId || selectedRole === null) return;

        await setOrganizationRole({
            variables: {
                organizationMemberId,
                isOrganizer: selectedRole === 'organizer',
                isEventHost: selectedRole === 'eventHost'
            }
        });

        setIsDialogOpen(false);
    };

    if (!organizationMemberId) {
        return (
            <div className={formStyles.formContainer}>
                <p className={formStyles.error}>No organization member ID provided</p>
            </div>
        );
    }

    if (memberLoading || questionsLoading) {
        return (
            <div className={formStyles.formContainer}>
                <p>Loading profile...</p>
            </div>
        );
    }

    if (memberError) {
        return (
            <div className={formStyles.formContainer}>
                <p className={formStyles.error}>Error loading profile: {memberError.message}</p>
            </div>
        );
    }

    if (!memberData?.organizationMember) {
        return (
            <div className={formStyles.formContainer}>
                <p className={formStyles.error}>Member profile not found</p>
            </div>
        );
    }

    const member = memberData.organizationMember;
    const questions = questionsData?.questions || [];

    const answersMap: Record<string, string> = {};
    member.answers?.forEach(answer => {
        answersMap[answer.organizationMembershipQuestionId] = answer.answer;
    });

    return (
        <div className={formStyles.formContainer}>
            <h1>{member.combinedProfile.username}</h1>
            
            <div>
                <div>
                    <Image
                        imageOwnerType={ImageOwnerType.Person}
                        imageSize={ImageSize.Profile}
                        imageId={member.combinedProfile.imageId}
                        className={styles.profilePicture}
                    />
                </div>
            </div>

            <div className={styles.memberOf}>
                <label>Member of:</label>
                <div className={styles.groupContainer}>
                {member.otherGroups.map((group) => (
                    <div key={group.organizationId} onClick={() => navigate(`/groups/${group.organizationId}`)} className={styles.groupItem}>
                        <Image
                            imageOwnerType={ImageOwnerType.Organization}
                            imageSize={ImageSize.Thumbnail}
                            imageId={group.imageId}
                            className={styles.groupThumbnail}
                        />
                        <p className={styles.groupName}>{group.name}</p>
                    </div>
                ))}
                </div>
            </div>


            <div>
                <label>Bio:</label>
                <p className={styles.textContainer}>{member.combinedProfile.bio || 'No bio provided'}</p>
            </div>

            <HasPermission groupId={member.organizationId} permission={Permission.MANAGE_GROUP}>
                <button onClick={handleOpenDialog} className={globalStyles.submitButton}>
                    Set Role
                </button>
            </HasPermission>

            {isDialogOpen && (
                <div className={globalStyles.modal}>
                    <div className={globalStyles.modalContent}>
                        <h2>Set Organization Role</h2>
                        <div>
                            <label>
                                <input
                                    type="radio"
                                    name="role"
                                    checked={selectedRole === 'organizer'}
                                    onChange={() => setSelectedRole('organizer')}
                                />
                                Organizer
                            </label>
                        </div>
                        <div>
                            <label>
                                <input
                                    type="radio"
                                    name="role"
                                    checked={selectedRole === 'eventHost'}
                                    onChange={() => setSelectedRole('eventHost')}
                                />
                                Event Host
                            </label>
                        </div>
                        <div className={globalStyles.buttonGroup}>
                            <button 
                                onClick={handleSaveRole} 
                                className={globalStyles.submitButton}
                                disabled={selectedRole === null}
                            >
                                Save
                            </button>
                            <button 
                                onClick={() => setIsDialogOpen(false)} 
                                className={globalStyles.cancelButton}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {questions.length > 0 && (
                <>
                    <h2>Membership Questions</h2>
                    {questions.map((question) => (
                        <div key={question.organizationMembershipQuestionId}>
                            <label>{question.question}</label>
                            <p className={styles.textContainer}>{answersMap[question.organizationMembershipQuestionId] || 'No answer provided'}</p>
                        </div>
                    ))}
                </>
            )}
        </div>
    );
};

export default ViewMemberProfile;

